import React, { useState, useEffect, useRef } from 'react';
import {
    Trash2, GripVertical, FileText, CheckCircle, ListChecks, ClipboardCheck,
    RotateCcw, Pencil, X, AlertTriangle, BookOpen, Clock, ListOrdered, GraduationCap,
    Clipboard, FolderOpen, FileCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { onboardingApi } from '@/lib/api/onboardingApi';
import { ModuleStepInput, ModuleStepType, GeneratedModule } from '@/lib/models/onboarding';
import { ActionButton, OutlineButton, IconChip, MarkdownEditor, LinkAssistPopover, IslandSlideOver } from '@/components/custom-ui';
import { sourcesApi } from '@/lib/api/sourcesApi';
import { ModuleFullResponse } from '@/lib/models/module';
import ModuleViewer from '@/components/modules/ModuleViewer';
import ModuleStepEditor from './ModuleStepEditor';
import LinkSeedPanel from './LinkSeedPanel';

// Parallel stable IDs for dnd-kit. We can't use array index (changes on reorder)
// or step content (mutates on edit), so we mint a UUID per row and keep it in
// lockstep with the steps array. Never sent to the backend.
const makeStepId = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);

// Thin sortable wrapper — keeps the actual row JSX in the parent (it depends
// on lots of parent state). The render-prop hands back drag-handle props so
// the parent can mount them onto the grip icon without touching the rest of
// the row's click behaviour.
const SortableStepRow: React.FC<{
    id: string;
    disabled?: boolean;
    children: (
        handleProps: React.HTMLAttributes<HTMLButtonElement>,
        isDragging: boolean,
    ) => React.ReactNode;
}> = ({ id, disabled, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
        disabled,
    });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
    };
    return (
        <div ref={setNodeRef} style={style}>
            {children({ ...attributes, ...(listeners as any) }, isDragging)}
        </div>
    );
};

const DRAFT_STORAGE_KEY = 'nexilink:onboarding_module_draft';

interface ModuleDraft {
    moduleName: string;
    moduleDescription: string;
    estimatedDuration: string;
    steps: ModuleStepInput[];
    savedAt: string;
}

const loadDraft = (): ModuleDraft | null => {
    try {
        const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as ModuleDraft;
    } catch {
        return null;
    }
};

const saveDraft = (draft: ModuleDraft) => {
    try {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch {
        // Ignore quota errors
    }
};

const clearDraft = () => {
    try {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch {
        // ignore
    }
};

const isDraftEmpty = (draft: ModuleDraft): boolean => {
    return !draft.moduleName.trim() &&
           !draft.moduleDescription.trim() &&
           !draft.estimatedDuration.trim() &&
           draft.steps.length === 0;
};

interface ManualModuleFormProps {
    onClose: () => void;
    onSuccess: () => void;
    // Edit mode: if these are provided, the form edits an existing module instead of creating a new one
    existingModuleId?: string;
    initialModuleName?: string;
    initialModuleDescription?: string;
    initialEstimatedDuration?: string;
    initialSteps?: ModuleStepInput[];
    // Skip draft loading/saving (used when reviewing AI-generated content)
    skipDraft?: boolean;
    // Show the in-canvas "Link seed" panel (the unified "Skapa modul" door).
    enableSeed?: boolean;
    // Buttons rendered in the header bar (e.g. "Chatta med Link", "Visa som student").
    headerActions?: React.ReactNode;
    // Lets the shell (CourseCanvasDrawer) route its backdrop/esc close through
    // the same unsaved-changes guard as the form's own Stäng/Avbryt controls.
    requestCloseRef?: React.MutableRefObject<(() => void) | null>;
}

const ManualModuleForm: React.FC<ManualModuleFormProps> = ({
    onClose,
    onSuccess,
    existingModuleId,
    initialModuleName,
    initialModuleDescription,
    initialEstimatedDuration,
    initialSteps,
    skipDraft,
    enableSeed,
    headerActions,
    requestCloseRef,
}) => {
    const isEditMode = !!existingModuleId;
    const draftDisabled = isEditMode || !!skipDraft;

    // Skip draft loading in edit mode or when explicitly disabled (e.g. AI review).
    const initialDraft = draftDisabled ? null : loadDraft();

    const [moduleName, setModuleName] = useState(initialModuleName ?? initialDraft?.moduleName ?? '');
    const [moduleDescription, setModuleDescription] = useState(initialModuleDescription ?? initialDraft?.moduleDescription ?? '');
    const [estimatedDuration, setEstimatedDuration] = useState(initialEstimatedDuration ?? initialDraft?.estimatedDuration ?? '');
    const [steps, setSteps] = useState<ModuleStepInput[]>(initialSteps ?? initialDraft?.steps ?? []);
    const [stepIds, setStepIds] = useState<string[]>(() =>
        (initialSteps ?? initialDraft?.steps ?? []).map(makeStepId)
    );
    const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasDraft, setHasDraft] = useState(!draftDisabled && !!initialDraft && !isDraftEmpty(initialDraft));
    const isFirstRender = useRef(true);

    // ── Link seed (the "Skapa modul" canvas door) ──────────────────────
    const canvasHasContent = !!moduleName.trim() || !!moduleDescription.trim() || steps.length > 0;
    const showSeed = !!enableSeed;

    // Load a Link-generated draft into the editable canvas state.
    const loadGenerated = (g: GeneratedModule) => {
        const newSteps = g.steps ?? [];
        setModuleName(g.module_name ?? '');
        setModuleDescription(g.module_description ?? '');
        setEstimatedDuration(g.estimated_duration ?? '');
        setSteps(newSteps);
        setStepIds(newSteps.map(makeStepId));
        setEditingStepIndex(null);
        setError(null);
    };

    // Auto-save draft to localStorage whenever form state changes (create mode only)
    useEffect(() => {
        // Skip auto-save in edit mode or when draft is disabled (AI review mode)
        if (draftDisabled) return;

        // Skip the first render so we don't immediately overwrite the initial draft
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const draft: ModuleDraft = {
            moduleName,
            moduleDescription,
            estimatedDuration,
            steps,
            savedAt: new Date().toISOString(),
        };

        if (isDraftEmpty(draft)) {
            clearDraft();
            setHasDraft(false);
        } else {
            saveDraft(draft);
            setHasDraft(true);
        }
    }, [moduleName, moduleDescription, estimatedDuration, steps, draftDisabled]);

    const handleDiscardDraft = () => {
        if (!confirm('Är du säker på att du vill ta bort utkastet? Detta går inte att ångra.')) {
            return;
        }
        clearDraft();
        setModuleName('');
        setModuleDescription('');
        setEstimatedDuration('');
        setSteps([]);
        setStepIds([]);
        setEditingStepIndex(null);
        setHasDraft(false);
    };

    // #320 — ModuleStepType has SEVEN members; these three maps listed four, so a
    // task / case / file_review step rendered with no icon, no label and no tone.
    // Link can author all seven and the candidate player renders all seven, so the
    // employer's own form was the only surface that could not name them. Icons and
    // labels match CourseStepsPreview so the same step reads the same in both.
    const stepTypeIcons: Record<ModuleStepType, LucideIcon> = {
        info: FileText,
        task: Clipboard,
        checkpoint: CheckCircle,
        quiz: ListChecks,
        confirmation: ClipboardCheck,
        case: FolderOpen,
        file_review: FileCheck,
    };

    const stepTypeLabels: Record<ModuleStepType, string> = {
        info: 'Information',
        task: 'Uppgift',
        checkpoint: 'Kontrollpunkt',
        quiz: 'Quiz',
        confirmation: 'Bekräftelse',
        case: 'Case',
        file_review: 'Filgranskning',
    };

    // Per-type colour so each step kind reads at a glance (matches the IconChip tones).
    const stepTypeTone: Record<ModuleStepType, 'blue' | 'green' | 'amber' | 'brand'> = {
        info: 'blue',
        task: 'brand',
        checkpoint: 'green',
        quiz: 'amber',
        confirmation: 'brand',
        case: 'blue',
        file_review: 'green',
    };

    const addStep = (type: ModuleStepType) => {
        let defaultContent: any;
        switch (type) {
            case 'info':
                defaultContent = { heading: '', body: '' };
                break;
            case 'checkpoint':
                defaultContent = {
                    heading: '',
                    question_text: '',
                    question_type: 'multiple_choice',
                    options: [
                        { option_id: 'A', text: '' },
                        { option_id: 'B', text: '' },
                    ],
                    correct_option_id: 'A',
                    explanation: '',
                };
                break;
            case 'quiz':
                defaultContent = {
                    questions: [],
                    pass_threshold: 1.0,
                };
                break;
            case 'confirmation':
                defaultContent = { heading: '', body: '', checkbox_label: '' };
                break;
        }

        const newStep: ModuleStepInput = {
            step_type: type,
            step_title: '',
            content: defaultContent,
        };
        setSteps([...steps, newStep]);
        setStepIds([...stepIds, makeStepId()]);
        setEditingStepIndex(steps.length);
    };

    const updateStep = (index: number, updated: ModuleStepInput) => {
        const newSteps = [...steps];
        newSteps[index] = updated;
        setSteps(newSteps);
    };

    const removeStep = (index: number) => {
        setSteps(steps.filter((_, i) => i !== index));
        setStepIds(stepIds.filter((_, i) => i !== index));
        if (editingStepIndex === index) setEditingStepIndex(null);
    };

    // dnd-kit drag end → swap steps + IDs together so they stay in sync.
    // Pointer activation has an 8px distance threshold so a click on the
    // drag handle still falls through to row buttons (regenerate, delete).
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const from = stepIds.indexOf(String(active.id));
        const to = stepIds.indexOf(String(over.id));
        if (from < 0 || to < 0) return;
        setSteps((prev) => arrayMove(prev, from, to));
        setStepIds((prev) => arrayMove(prev, from, to));
        // Keep the editing step open across reorder by translating the index.
        setEditingStepIndex((prev) => {
            if (prev === null) return null;
            if (prev === from) return to;
            // If the editing row was between the moved range, shift by one.
            if (from < prev && prev <= to) return prev - 1;
            if (to <= prev && prev < from) return prev + 1;
            return prev;
        });
    };

    const handleSubmit = async () => {
        if (!moduleName.trim()) {
            setError('Modulnamn krävs');
            return;
        }
        if (!moduleDescription.trim()) {
            setError('Modulbeskrivning krävs');
            return;
        }
        if (steps.length === 0) {
            setError('Lägg till minst ett steg');
            return;
        }
        for (let i = 0; i < steps.length; i++) {
            if (!steps[i].step_title.trim()) {
                setError(`Steg ${i + 1} saknar titel`);
                return;
            }
        }

        setIsSubmitting(true);
        setError(null);
        try {
            const payload = {
                module_name: moduleName.trim(),
                module_description: moduleDescription.trim(),
                estimated_duration: estimatedDuration.trim() || undefined,
                steps,
            };

            if (isEditMode && existingModuleId) {
                await onboardingApi.updateOnboardingModule(existingModuleId, payload);
            } else {
                await onboardingApi.createOnboardingModule(payload);
                clearDraft();
            }
            onSuccess();
        } catch (err: any) {
            setError(err?.message || 'Något gick fel.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Unsaved-changes guard ────────────────────────────────────────────
    // Mirrors the 3a needs editor: compare a snapshot of the editable fields to
    // a baseline captured on mount; prompt before closing when they differ.
    const coreSnapshot = () => JSON.stringify({ moduleName, moduleDescription, estimatedDuration, steps });
    const baselineRef = useRef<string | null>(null);
    const snapRef = useRef<string>('');
    snapRef.current = coreSnapshot();
    const didInit = useRef(false);
    if (!didInit.current) {
        didInit.current = true;
        baselineRef.current = snapRef.current;
    }
    const isDirty = () => baselineRef.current !== null && baselineRef.current !== snapRef.current;

    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const requestClose = () => {
        if (isDirty()) setShowExitConfirm(true);
        else onClose();
    };
    // Expose the guard so the shell's backdrop/esc close routes through it too.
    if (requestCloseRef) requestCloseRef.current = requestClose;

    const discardAndClose = () => { setShowExitConfirm(false); onClose(); };
    const saveThenClose = async () => {
        setShowExitConfirm(false);
        await handleSubmit(); // closes via onSuccess on success; stays open (with error) on validation failure
    };

    // ── Live student preview ─────────────────────────────────────────────
    // Renders the interactive player from the CURRENT (unsaved) editor state so
    // reorders/edits show immediately — not the last saved version.
    const [previewOpen, setPreviewOpen] = useState(false);
    const buildPreviewData = (): ModuleFullResponse => ({
        id: existingModuleId ?? 'preview',
        module_name: moduleName || 'Namnlös modul',
        module_description: moduleDescription,
        case_ids: [],
        module_type: 'onboarding',
        is_mandatory: false,
        estimated_duration: estimatedDuration || undefined,
        created_at: '', created_by: '', updated_at: '', updated_by: '',
        steps: steps.map((s, i) => ({
            id: `preview-${i}`,
            module_id: existingModuleId ?? 'preview',
            step_order: i,
            step_type: s.step_type,
            step_title: s.step_title,
            content: s.content,
            evaluation_mode: (s as any).evaluation_mode ?? 'self_check',
            sub_points: (s as any).sub_points ?? [],
            phase: (s as any).phase,
            created_at: '', updated_at: '',
        })),
        cases: {},
    } as ModuleFullResponse);

    return (
        <div className="flex flex-col h-full">
            {/* Header bar */}
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Pencil className="w-4 h-4 text-[#1A4D4F]" /> {isEditMode ? 'Redigera modul' : 'Skapa modul'}
                </div>
                <div className="flex items-center gap-2">
                    {headerActions}
                    <OutlineButton
                        icon={GraduationCap}
                        onClick={() => setPreviewOpen(true)}
                        disabled={steps.length === 0}
                        title={steps.length === 0 ? 'Lägg till minst ett steg för att förhandsgranska' : undefined}
                    >
                        Visa som student
                    </OutlineButton>
                    <button onClick={requestClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Stäng">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50/40 min-h-0">
                <div className={`mx-auto flex flex-col lg:flex-row gap-6 ${showSeed ? 'max-w-6xl' : 'max-w-3xl'}`}>
                    {/* Link side-rail — sits on top on small screens, becomes the
                        right column on desktop (order-last). */}
                    {showSeed && (
                        <aside className="order-first lg:order-last w-full lg:w-80 xl:w-96 flex-shrink-0 lg:self-start lg:sticky lg:top-0">
                            <LinkSeedPanel
                                onSeeded={loadGenerated}
                                hasContent={canvasHasContent}
                                defaultOpen={!canvasHasContent}
                            />
                        </aside>
                    )}

                    {/* Main column */}
                    <div className="flex-1 min-w-0 space-y-6">
                        {/* Restored-draft notice (create mode only) */}
                        {!draftDisabled && hasDraft && (
                            <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                                <span className="flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Ett sparat utkast återställdes — det sparas automatiskt medan du arbetar.</span>
                                <button type="button" onClick={handleDiscardDraft} className="font-medium text-amber-900 hover:underline flex-shrink-0">Rensa utkast</button>
                            </div>
                        )}

                        {/* Header — mirrors ModuleView (icon chip + eyebrow + name + meta) */}
                        <div className="flex items-start gap-3">
                            <IconChip icon={BookOpen} tone="brand" size="lg" />
                            <div className="flex-1 min-w-0">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#1A4D4F]/70">Modul</p>
                                <input
                                    type="text"
                                    value={moduleName}
                                    onChange={(e) => setModuleName(e.target.value)}
                                    placeholder="Modulnamn — t.ex. Introduktion till vår verksamhet"
                                    className="w-full text-2xl font-bold text-gray-900 bg-transparent border-b border-transparent hover:border-gray-200 focus:border-[#1A4D4F] focus:outline-none placeholder:text-gray-300 placeholder:font-normal"
                                />
                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#A4C3B2]/20 text-[#1A4D4F]">
                                        <ListOrdered className="w-3 h-3" /> {steps.length} steg
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                                        <input
                                            type="text"
                                            value={estimatedDuration}
                                            onChange={(e) => setEstimatedDuration(e.target.value)}
                                            placeholder="Uppskattad tid (valfritt)"
                                            className="w-40 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-[#1A4D4F] focus:outline-none placeholder:text-gray-300 text-gray-600"
                                        />
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Beskrivning — TipTap (mirrors the rendered body), with Link assist */}
                        <section>
                            <h3 className="text-base font-semibold text-gray-900 mb-2">Beskrivning</h3>
                            <MarkdownEditor
                                value={moduleDescription}
                                onChange={(md) => setModuleDescription(md)}
                                placeholder="Vad handlar modulen om? Vem är den för, och vad lär man sig?"
                                minHeightClass="min-h-[160px]"
                                onImageUpload={async (file) => {
                                    const src = await sourcesApi.uploadSource(file);
                                    return sourcesApi.getPublicImageUrl(src.id);
                                }}
                                selectionAssistRun={(action, instruction, selectedText) => onboardingApi.aiAssistModule({
                                    action: action as any,
                                    content: selectedText,
                                    instruction,
                                    module_name: moduleName,
                                    scope: 'selection',
                                })}
                                toolbarExtra={
                                    <LinkAssistPopover
                                        subjectText={moduleDescription}
                                        run={(action, instruction) => onboardingApi.aiAssistModule({
                                            action: action as any,
                                            content: moduleDescription,
                                            instruction,
                                            module_name: moduleName,
                                        })}
                                        onApply={(revised) => setModuleDescription(revised)}
                                    />
                                }
                            />
                        </section>

                        {/* Modulinnehåll — a connected learning path (mirrors ModuleView) */}
                        <section>
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <ListChecks className="w-4 h-4 text-[#1A4D4F]" /> Modulinnehåll
                                <span className="text-gray-400 font-normal">({steps.length})</span>
                            </h3>

                            {steps.length > 0 && (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext items={stepIds} strategy={verticalListSortingStrategy}>
                                        <div className="relative space-y-3 mb-4">
                                            {/* the vertical path line, centred under the number nodes */}
                                            {steps.length > 1 && (
                                                <span className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-[#A4C3B2]/50" aria-hidden />
                                            )}
                                            {steps.map((step, index) => {
                                                const Icon = stepTypeIcons[step.step_type];
                                                const isEditing = editingStepIndex === index;

                                                return (
                                                    <SortableStepRow
                                                        key={stepIds[index]}
                                                        id={stepIds[index]}
                                                    >
                                                        {(dragHandleProps, isDragging) => (
                                                            <div className="relative flex items-start gap-3">
                                                                {/* number node on the path */}
                                                                <span className="relative z-[1] flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-white border-2 border-[#1A4D4F] text-[#1A4D4F] text-xs font-bold">
                                                                    {index + 1}
                                                                </span>
                                                                <div
                                                                    className={`min-w-0 flex-1 border rounded-xl bg-white ${isDragging ? 'shadow-lg' : ''} ${isEditing ? 'border-[#1A4D4F] shadow-sm' : 'border-gray-200'}`}
                                                                >
                                                                    <div
                                                                        className="flex items-center gap-3 p-3 cursor-pointer"
                                                                        onClick={() => setEditingStepIndex(isEditing ? null : index)}
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            {...dragHandleProps}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            title="Dra för att ändra ordning"
                                                                            className="touch-none cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 -ml-1 flex-shrink-0"
                                                                        >
                                                                            <GripVertical className="w-4 h-4" />
                                                                        </button>
                                                                        <IconChip icon={Icon} size="sm" tone={stepTypeTone[step.step_type as ModuleStepType]} />
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                                                {step.step_title || `Nytt ${stepTypeLabels[step.step_type as ModuleStepType].toLowerCase()}-steg`}
                                                                            </p>
                                                                            <p className="text-xs text-gray-500">{stepTypeLabels[step.step_type as ModuleStepType]}</p>
                                                                        </div>
                                                                        <div className="flex items-center gap-1">
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => { e.stopPropagation(); removeStep(index); }}
                                                                                className="p-1 text-red-500 hover:bg-red-100 rounded"
                                                                            >
                                                                                <Trash2 className="w-4 h-4" />
                                                                            </button>
                                                                        </div>
                                                                    </div>

                                                                    {isEditing && (
                                                                        <div className="px-3 pb-3 border-t border-gray-100 pt-3">
                                                                            <ModuleStepEditor
                                                                                step={step}
                                                                                moduleName={moduleName}
                                                                                onChange={(updated) => updateStep(index, updated)}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </SortableStepRow>
                                                );
                                            })}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            )}

                            {/* Add step buttons — each carries its type's colour so the
                                four step kinds read at a glance. */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {(['info', 'checkpoint', 'quiz', 'confirmation'] as ModuleStepType[]).map((type) => {
                                    const Icon = stepTypeIcons[type];
                                    return (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => addStep(type)}
                                            className="flex flex-col items-center gap-2 p-3.5 border border-dashed border-gray-300 hover:border-[#1A4D4F] hover:bg-[#A4C3B2]/20 rounded-xl transition-all group"
                                        >
                                            <IconChip icon={Icon} size="sm" tone={stepTypeTone[type]} />
                                            <span className="text-xs font-medium text-gray-700 group-hover:text-gray-900">{stepTypeLabels[type]}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </section>

                        {error && <p className="text-sm text-red-600">{error}</p>}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-white">
                <div className="flex gap-3 ml-auto">
                    <OutlineButton onClick={requestClose}>Avbryt</OutlineButton>
                    {/* In edit mode, disable Save until something actually changed —
                        clearer UX + a client-side guard behind the server no-op check. */}
                    <ActionButton onClick={handleSubmit} disabled={isSubmitting || (isEditMode && !isDirty())} loading={isSubmitting}>
                        {isEditMode ? 'Spara ändringar' : 'Skapa modul'}
                    </ActionButton>
                </div>
            </div>

            {/* Unsaved-changes guard */}
            {showExitConfirm && (
                <div
                    className="absolute inset-0 z-[60] flex items-center justify-center bg-black/30 p-4"
                    onClick={() => setShowExitConfirm(false)}
                >
                    <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" /> Osparade ändringar
                        </h3>
                        <p className="text-sm text-gray-500 mt-1.5">Du har ändringar som inte sparats. Vill du spara dem?</p>
                        <div className="mt-4 space-y-2">
                            <ActionButton onClick={saveThenClose} loading={isSubmitting} className="w-full">
                                {isEditMode ? 'Spara ändringar' : 'Skapa modul'}
                            </ActionButton>
                            <div className="flex gap-2">
                                <button type="button" onClick={discardAndClose} className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
                                    Stäng utan att spara
                                </button>
                                <button type="button" onClick={() => setShowExitConfirm(false)} className="flex-1 px-3 py-2 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-50">
                                    Avbryt
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* "Visa som student" — the real player rendered from the LIVE editor
                state (unsaved edits included), as a full-screen overlay above the
                editor so closing returns to exactly where you were. */}
            {previewOpen && (
                <IslandSlideOver onClose={() => setPreviewOpen(false)} fullScreen zClass="z-[60]">
                    <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 flex-shrink-0">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                            <GraduationCap className="w-4 h-4 text-[#1A4D4F]" /> Studentvy — {moduleName || 'Namnlös modul'}
                        </div>
                        <button onClick={() => setPreviewOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6 bg-gray-50/40">
                        <div className="max-w-3xl mx-auto">
                            <ModuleViewer previewData={buildPreviewData()} preview onClose={() => setPreviewOpen(false)} />
                        </div>
                    </div>
                </IslandSlideOver>
            )}
        </div>
    );
};

export default ManualModuleForm;
